import { useSearchParams } from 'react-router-dom';

import { resolveStudyToolYear, STUDY_YEARS } from './studyToolYear';

export type ChemistryYear = (typeof STUDY_YEARS)[number];

/**
 * Year view for a Chemistry tool, read from `?year`. Browsing is URL-scoped and never writes the
 * Study profile. Returns null when the URL has no supported Year (or `year=all`), so a tool can
 * fall back to its own default mode (study-chemistry-tools-prd.md §5 CT-MODE).
 */
export function useChemistryToolYear(): ChemistryYear | null {
  const [searchParams] = useSearchParams();
  const value = searchParams.get('year');
  return value === null ? null : resolveStudyToolYear(value, null);
}

/**
 * Picks the highest mode whose start Year is at or below the learner Year. Modes are listed by
 * ascending start Year. Without a Year, the first (most foundational) mode is used.
 */
export function modeForYear<Mode extends string>(
  modes: readonly { mode: Mode; fromYear: number }[],
  year: number | null,
): Mode {
  if (modes.length === 0) throw new Error('modeForYear needs at least one mode');
  if (year === null) return modes[0].mode;
  const eligible = modes.filter((entry) => entry.fromYear <= year);
  return (eligible.length > 0 ? eligible[eligible.length - 1] : modes[0]).mode;
}
